/**
 * Inspired by the following work:
 *
 * Copyright (c) 2017 Randy Lebeau.
 *
 * https://github.com/SupremeTechnopriest/react-idle-timer
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
import React, { memo, useEffect, useState } from 'react';
import { IdleTimerProps } from './index.d';

const IdleTimer: React.FC<IdleTimerProps> = memo(
  ({
    timeout = 1000 * 60 * 60,
    children,
    idleCallback = () => false,
    eventListeners = [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mouseWheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'MSPointerDown',
      'MSPointerMove',
    ],
  }) => {
    const [isIdle, setIsIdle] = useState<boolean>(false);
    const [supportPassive, setSupportPassive] = useState();

    let timer = null;

    const reset = () => {
      // reset timers
      clearTimeout(timer);
      setIsIdle(false);
      // reset settings
      timer = setTimeout(triggerIdleCallback, timeout);
    };
    const setListeners = (addOrRemove: string, callback: (x) => any) => (
      events: string[],
    ) => {
      events.forEach(e =>
        document[addOrRemove](
          e,
          callback,
          addOrRemove === 'addEventListener'
            ? supportPassive
              ? { passive: true }
              : false
            : false,
        ),
      );
    };

    const addEventListeners = setListeners('addEventListener', reset);
    const removeEventListeners = setListeners('removeEventListener', reset);

    useEffect(() => {
      try {
        const opts = Object.defineProperty({}, 'passive', {
          get: () => {
            // @ts-ignore
            setSupportPassive(true);
          },
        });
        window.addEventListener('testPassive', null, opts);
        window.removeEventListener('testPassive', null, opts);
      } catch (e) {
        // silence
      }

      // eslint-disable-next-line
      timer = setTimeout(triggerIdleCallback, timeout);
      // bind event listeners so that our internal timer will be reset when any of them fire
      addEventListeners(eventListeners);

      return () => {
        // Clear timeout to prevent delayed state changes
        clearTimeout(timer);
        // Unbind all events
        removeEventListeners(eventListeners);
      };
    }, []);

    const triggerIdleCallback = () => {
      // clear any existing timeout
      clearTimeout(timer);
      // remove the listeners since we have triggered the idle callback
      removeEventListeners(eventListeners);
      // call the idle callback and pass a reference to the reset function
      // in case we want to re-initiate the timer again
      setIsIdle(true);

      idleCallback(restore);
    };

    const restore = () => {
      addEventListeners(eventListeners);
      reset();
    };

    return children ? children(isIdle, restore) : null;
  },
);

export default IdleTimer;
